import { FORM_SETTING_TYPE } from './CRMIndexedDBTypes';

export const VEEVA_VAULT_TABLES = {
  CALL: 'call2__v',
  CALL_DETAIL: 'call2_detail__v',
  CALL_DISCUSSION: 'call2_discussion__v',
  MEDICAL_DISCUSSION: 'medical_discussion__v',
  MEDICAL_INSIGHT: 'medical_insight__v',
  MEDICAL_INQUIRY: 'medical_inquiry__v',
};

export enum SUPPORTED_VEEVA_CONTROL_TYPES {
  ATTENDEE_SECTION = 'call_attendees_section__v',
  MEDICAL_DISCUSSION_SECTION = 'medical_discussion_section__v',
  CALL_DETAIL_SECTION = 'call_details_section__v',
  CALL_DISCUSSION_SECTION = 'product_discussion_section__v',
  BUSINESS_ACCOUNT = 'business_account__v',
  DELIVERY_METHOD = 'delivery_method_field_control__v',
}

export enum SPECIAL_FIELDS {
  USERS_ATTENDEES = 'call_attendees_section__v_user__sys',
}

/* API RESPONSE TYPES */
export type VeevaVaultObjectMetadata = {
  help_content?: string
  description?: string
  label: string
  label_plural?: string
  name: string
  fields: VeevaVaultObjectField[]
  relationships: {
    field: string
    relationship_name: string
    relationship_label: string
    object: {
      name: string
    }
  }[]
}

// NOTE: EVEN THOUGH WE HAVE THE "relationships" FIELD WITHIN THE OBJECTMETADATA,
// IT IS POSSIBLE THAT AN OBJECT HAS A RELATIONSHIP WITH "ITS PARENT" MULTIPLE TIMES.
// TO AVOID THE POSSIBILITY OF PICKING UP THE WRONG FIELD, WE DEFINE IT DIRECTLY.
export const VeevaVaultParentRelationshipFields = {
  [VEEVA_VAULT_TABLES.CALL]: 'parent_call__v',
  [VEEVA_VAULT_TABLES.CALL_DETAIL]: 'call2__v',
  [VEEVA_VAULT_TABLES.CALL_DISCUSSION]: 'call2__v',
  [VEEVA_VAULT_TABLES.MEDICAL_DISCUSSION]: 'interaction__v',
  [VEEVA_VAULT_TABLES.MEDICAL_INSIGHT]: 'interaction__v',
}

export type VeevaVaultObjectField = {
  controlling_picklist?: string
  help_content?: string
  editable?: boolean
  multi_value?: boolean
  lookup_relationship_name?: string
  lookup_source_field?: string
  relationship_outbound_name?: string
  picklist?: string // NAME OF THE PICKLIST (NOT THE FIELD)
  relationship_type?: VeevaVaultFieldRelationshipType
  description?: string
  default_value?: string
  label: string
  label_plural?: string
  max_length?: number
  required?: boolean
  name: string
  type: VeevaVaultObjectFieldType
  source: VeevaVaultObjectFieldSource
  object?: VeevaVaultObjectReference
  picklist_dependencies?: {
    picklistDependencies: { [key: string]: string[] }
  }
}

export enum VeevaVaultFieldRelationshipType {
  PARENT = 'parent',
  REFERENCE = 'reference',
  REFERENCE_OUTBOUND = 'reference_outbound',
  REFERENCE_INBOUND = 'reference_inbound',
}

export enum VeevaVaultObjectFieldSource {
  STANDARD = 'standard',
  SYSTEM = 'system',
  CUSTOM = 'custom',
}

export enum VeevaVaultObjectFieldType {
  OBJECT = 'Object',
  STRING = 'String',
  PICKLIST = 'Picklist',
  DATETIME = 'DateTime',
  COMPONENT = 'Component',
  RICH_TEXT = 'RichText',
  LONG_TEXT = 'LongText',
  NUMBER = 'Number',
  ID = 'ID',
  DATE = 'Date',
  BOOLEAN = 'Boolean',
}

/* LAYOUT */
export enum VeevaVaultSectionLayoutType {
  ONE_COLUMN = 'One-Column',
  TWO_COLUMNS = 'Two-Columns'
}

export enum VeevaVaultItemType {
  FIELD = 'field',
  CONTROL = 'control',
  SPACER = 'spacer',
}

export enum VeevaVaultSectionType {
  CONTROL = 'control',
  DETAIL = 'detail',
}

export enum VeevaVaultFieldStatus {
  ACTIVE = 'active__v'
}

export type VeevaVaultObjectLayout = {
  name: string
  label: string
  object: string
  object_type: string
  active: boolean
  description?: string
  sections: VeevaVaultObjectLayoutSection[]
  layout_rules: VeevaVaultLayoutRule[]
  permissions: {
    create: boolean,
    read: boolean,
    delete: boolean,
    edit: boolean,
  }
}

export type VeevaVaultLayoutRule = {
  fields_to_hide?: string[]
  sections_to_hide?: string[]
  controls_to_hide?: string
  status: VeevaVaultFieldStatus
  displayed_as_required_fields?: string[]
  displayed_as_readonly_fields?: string[]
}

export type VeevaVaultObjectLayoutSection = {
  name: string
  title: string
  type: VeevaVaultSectionType,
  help_content?: string
  properties: {
    relationship?: string
    related_object?: string
    layout_type?: VeevaVaultSectionLayoutType
    items?: VeevaVaultSectionLayoutItem[]
    columns?: VeevaVaultSectionColumn[]
    controls?: VeevaVaultLayoutSectionControl[]
  }
}

export type VeevaVaultLayoutSectionControl = {
  reference: string
  name: string
  bindings: {
    // THIS INVOLVES ADDITIONAL PROPERTIES THAT THE SECTION MIGHT HAVE
    // EX: { property: 'su', value: 'true' } = ADDS USER SEARCH
    property: string
    value: string | string[]
  }[]
}

export type VeevaVaultSectionColumn = {
  reference: string // FIELD NAME
  status: VeevaVaultFieldStatus
}

export type VeevaVaultSectionLayoutItem = {
  type: VeevaVaultItemType
  reference?: string // FIELD NAME
  required?: boolean
  read_only?: boolean
  status?: VeevaVaultFieldStatus
  controls?: VeevaVaultSectionLayoutItemControl[]
}

export type VeevaVaultSectionLayoutItemControl = {
  reference: string
  name: string
  bindings: {
    property: string
    value: {
      fieldLabel: string,
      fieldName: string
    }
  }[]
}

type VeevaVaultObjectReference = {
  label: string
  name: string
}

export type VeevaVaultPicklists = {
  [key: string]: VeevaVaultPicklist
}

export type VeevaVaultPicklist = {
  controllingPicklistName?: string
  picklistDependencies?: { [key: string]: string[] }
  picklistValues: VeevaVaultPicklistValues[]
}

type VeevaVaultPicklistValues = {
  name: string
  label: string
}

export type VeevaVaultLookups = {
  [key: string]: VeevaVaultLookup[]
}

export type VeevaVaultLookup = {
  id: string
  name__v: string
  [fieldName: string]: string;
}

export type VeevaVaultFormSetting = {
  id: string;
  apiName: string;
  objectType?: string;
  picklists: VeevaVaultPicklists;
  lookups: VeevaVaultLookups;
  layout: VeevaVaultObjectLayout;
  label: string;
  layoutLabel?: string;
  objectInfo: VeevaVaultObjectMetadata;
  isMainTable?: boolean;
  isStandaloneForm?: boolean;
  parentTableRelationshipName?: string;
  formSettingType: FORM_SETTING_TYPE.VEEVA_VAULT;
}

export type VeevaVaultDeleteObjectsOutput = {
  status: 'rejected' | 'fulfilled',
  reason?: string
}

/* UPSERT/DELETE RESPONSES */
export type VeevaVaultUpsertObjectsOutput = {
  data?: VeevaVaultUpsertedObjectResponse[],
  errors?: VeevaVaultRequestError[],
  responseStatus: VeevaVaultResponseStatus,
}

type VeevaVaultRequestError = {
  type: string
  message: string
}

type VeevaVaultUpsertedObjectResponse = {
  responseStatus: VeevaVaultResponseStatus,
  errors?: VeevaVaultRequestError[],
  data: VeevaVaultUpsertedObject
}

type VeevaVaultUpsertedObject = {
  id: string
}

export enum VeevaVaultResponseStatus {
  SUCCESS = 'SUCCESS',
  EXCEPTION = 'EXCEPTION', // SYSTEM ERROR
  FAILURE = 'FAILURE', // USER ERROR
  WARNING = 'WARNING',
}

/* UPSERT/DELETE PAYLOAD */
export type VeevaVaultUpsertObjectPayload = {
  objectAPIName: string
  object: VeevaVaultUpsertRecord
  referenceId: string
};

export type VeevaVaultDeleteObjectsPayload = {
  [apiName: string]: VeevaVaultObjectToDeletePayload[]
};

export type VeevaVaultObjectToDeletePayload = {
  beaconId: string;
  externalId: string;
}

export type VeevaVaultUpsertRecord = {
  [fieldName: string]: number | null | boolean | string | string[]
}
